import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import toast from "react-hot-toast";
import {
  FiUsers,
  FiAward,
  FiSearch,
  FiFilter,
  FiCheckCircle,
  FiActivity,
  FiTrendingUp,
  FiFileText,
  FiClock,
  FiInfo
} from "react-icons/fi";
import { useTheme } from "../../context/ThemeContext";

const StudentDSAReports = () => {
  const { isDark } = useTheme();
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    totalStudents: 0,
    activeStudents: 0,
    averageSolved: 0,
    totalQuestions: 0
  });
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedBranch, setSelectedBranch] = useState("All");
  const [selectedSection, setSelectedSection] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchReports();
  }, [selectedBranch, selectedSection]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(`${baseApiURL()}/dsa/faculty-reports`, {
        params: {
          branch: selectedBranch,
          section: selectedSection
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setReports(response.data.reports || []);
        setSummary(response.data.summary || {});
      }
    } catch (error) {
      console.error("Error fetching faculty DSA reports:", error);
      toast.error("Failed to load student DSA reports");
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = reports.filter((student) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      student.name.toLowerCase().includes(query) ||
      student.enrollmentNo.toLowerCase().includes(query) ||
      student.branch.toLowerCase().includes(query)
    );
  });

  const topStudents = reports.slice(0, 3);

  return (
    <div className="space-y-6 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 min-h-screen p-4 md:p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs dark:shadow-2xl transition-colors duration-250">
      {/* 🌟 Header Banner */}
      <div className="bg-white dark:bg-gradient-to-r dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 text-slate-900 dark:text-white shadow-xs dark:shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 text-xs font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full border border-indigo-100 dark:border-indigo-500/20">
                Faculty Portal
              </span>
              <span className="px-3 py-1 text-xs font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-full border border-amber-200 dark:border-amber-500/20">
                Student DSA & Coding Reports
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight mt-2 text-slate-900 dark:text-white">
              Student LeetCode Analytics & Leaderboard
            </h1>
            <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
              Monitor student coding problem completion, track topic progress, and review individual performance reports.
            </p>
          </div>

          <div className="flex items-center space-x-3 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-500/20 border border-amber-200 dark:border-amber-500/30 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-xl shadow-xs">
              <FiAward />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase">Total Pool Questions</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{summary.totalQuestions || 0} Problems</p>
            </div>
          </div>
        </div>
      </div>

      {/* 📊 Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Enrolled Students</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{summary.totalStudents || 0}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-100 dark:border-indigo-500/20">
            <FiUsers className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Active Problem Solvers</p>
            <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{summary.activeStudents || 0}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-100 dark:border-emerald-500/20">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Avg Solved Per Student</p>
            <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400 mt-1">{summary.averageSolved || 0} Questions</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-100 dark:border-indigo-500/20">
            <FiTrendingUp className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* 🏆 Top Performers Leaderboard */}
      {topStudents.length > 0 && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs space-y-4">
          <div className="flex items-center space-x-2 text-slate-900 dark:text-white font-bold text-base">
            <FiAward className="w-5 h-5 text-amber-500 dark:text-amber-400" />
            <span>Top Performing Students Leaderboard</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topStudents.map((st, idx) => (
              <div
                key={st.enrollmentNo}
                className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center space-x-3 transition-all"
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm text-white ${
                    idx === 0
                      ? "bg-amber-500 shadow-md shadow-amber-500/30"
                      : idx === 1
                      ? "bg-slate-500 dark:bg-slate-600"
                      : "bg-amber-700"
                  }`}
                >
                  #{idx + 1}
                </div>
                <div className="flex-1 truncate">
                  <p className="font-bold text-slate-900 dark:text-white text-sm truncate">{st.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{st.enrollmentNo} • {st.branch}</p>
                </div>
                <div className="text-right">
                  <span className="text-base font-black text-indigo-600 dark:text-indigo-400">{st.totalSolved}</span>
                  <p className="text-[10px] text-slate-400 font-medium uppercase">Solved</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 🔍 Search & Filters */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Search student by name or enrollment..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-all font-medium"
          />
        </div>

        <div className="flex items-center space-x-3 w-full md:w-auto">
          <select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            className="px-3.5 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 cursor-pointer"
          >
            <option value="All">All Branches</option>
            <option value="CSE">CSE</option>
            <option value="ECE">ECE</option>
            <option value="EEE">EEE</option>
            <option value="IT">IT</option>
            <option value="MECH">MECH</option>
          </select>

          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="px-3.5 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 cursor-pointer"
          >
            <option value="All">All Sections</option>
            <option value="A">Section A</option>
            <option value="B">Section B</option>
            <option value="C">Section C</option>
            <option value="D">Section D</option>
          </select>
        </div>
      </div>

      {/* 📋 Student Reports Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-500"></div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Generating student DSA reports...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="p-12 text-center text-slate-500 dark:text-slate-400 space-y-2">
            <FiInfo className="w-10 h-10 text-slate-400 dark:text-slate-600 mx-auto" />
            <p className="font-semibold text-sm text-slate-900 dark:text-white">No student records found matching filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 text-[11px] font-bold">
                  <th className="py-3.5 px-4">Rank</th>
                  <th className="py-3.5 px-4">Student Name</th>
                  <th className="py-3.5 px-4">Enrollment No</th>
                  <th className="py-3.5 px-4">Branch / Sec</th>
                  <th className="py-3.5 px-4 text-center">Total Solved</th>
                  <th className="py-3.5 px-4 text-center">Easy</th>
                  <th className="py-3.5 px-4 text-center">Medium</th>
                  <th className="py-3.5 px-4 text-center">Hard</th>
                  <th className="py-3.5 px-4">Completion %</th>
                  <th className="py-3.5 px-4 text-center">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 bg-white dark:bg-slate-900">
                {filteredReports.map((st, index) => (
                  <tr key={st.enrollmentNo} className="hover:bg-slate-50 dark:hover:bg-slate-850 transition-colors border-b border-slate-100 dark:border-slate-800/60">
                    <td className="py-3.5 px-4 font-bold text-slate-500 dark:text-slate-400 text-center w-12">
                      #{index + 1}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">{st.name}</td>
                    <td className="py-3.5 px-4 font-mono text-slate-600 dark:text-slate-400">{st.enrollmentNo}</td>
                    <td className="py-3.5 px-4 font-semibold text-slate-700 dark:text-slate-300">
                      {st.branch || "CSE"} {st.section ? `- ${st.section}` : ""}
                    </td>
                    <td className="py-3.5 px-4 text-center font-black text-indigo-600 dark:text-indigo-400 text-sm">
                      {st.totalSolved}
                    </td>
                    <td className="py-3.5 px-4 text-center font-semibold text-emerald-600 dark:text-emerald-400">
                      {st.easySolved}
                    </td>
                    <td className="py-3.5 px-4 text-center font-semibold text-amber-600 dark:text-amber-400">
                      {st.mediumSolved}
                    </td>
                    <td className="py-3.5 px-4 text-center font-semibold text-rose-600 dark:text-rose-400">
                      {st.hardSolved}
                    </td>
                    <td className="py-3.5 px-4 w-44">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-slate-100 dark:bg-slate-950 h-2 rounded-full overflow-hidden border border-slate-200 dark:border-slate-800">
                          <div
                            className="bg-indigo-600 dark:bg-indigo-500 h-full rounded-full transition-all"
                            style={{ width: `${st.completionPercentage}%` }}
                          ></div>
                        </div>
                        <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{st.completionPercentage}%</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-center text-slate-500 dark:text-slate-400 text-[11px]">
                      {st.lastActive ? new Date(st.lastActive).toLocaleDateString() : "Never"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDSAReports;
